// AuthContext.jsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { authAPI } from "../services/api";
import {
  initSocket,
  connectSocket,
  disconnectSocket,
} from "../services/socket";
import {
  registerForPushNotifications,
  unsubscribeFromPushNotifications,
} from "../services/pushNotifications";
import {
  clearStoredAuthToken,
  getStoredAuthToken,
  persistAuthToken,
} from "../utils/authStorage";

const AuthContext = createContext(null);
const MANUAL_LOGOUT_FLAG = "task-suite:manual-logout";

const clearManualLogoutFlag = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(MANUAL_LOGOUT_FLAG);
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);

  const socketInitializedRef = useRef(false);

  /* ---------------------------------------------------
     LOGOUT
  --------------------------------------------------- */
  const handleLogout = useCallback(() => {
    unsubscribeFromPushNotifications().catch((err) => {
      console.warn("Push unsubscribe failed:", err?.message || err);
    });

    disconnectSocket();
    socketInitializedRef.current = false;

    clearStoredAuthToken();
    localStorage.removeItem("user");
    sessionStorage.setItem(MANUAL_LOGOUT_FLAG, String(Date.now()));

    setUser(null);
    setSocketConnected(false);

    window.location.replace("/");
  }, []);

  /* ---------------------------------------------------
     SOCKET INITIALIZATION (CRITICAL FIX)
  --------------------------------------------------- */
  useEffect(() => {
    if (!user?._id || user?.isGuest) {
      setSocketConnected(false);
      return;
    }
    if (socketInitializedRef.current) return;

    const socket = initSocket();
    if (!socket) return;

    const handleConnect = () => {
      console.log("✅ AuthContext: Socket connected");
      setSocketConnected(true);
    };

    const handleDisconnect = () => {
      console.log("⚠️ AuthContext: Socket disconnected");
      setSocketConnected(false);
    };

    const handleConnectError = (err) => {
      console.error("❌ AuthContext: Socket error:", err.message, err.description);
      setSocketConnected(false);
      if (err.message === "Authentication failed" || err.message === "Authentication token required") {
        const currentToken = getStoredAuthToken();
        if (!currentToken) {
          disconnectSocket();
          socketInitializedRef.current = false;
        }
      }
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    socketInitializedRef.current = true;
    connectSocket();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socketInitializedRef.current = false;
    };
  }, [user?._id, user?.isGuest]);

  useEffect(() => {
    if (!user?._id || user?.isGuest) return;
    registerForPushNotifications({ askPermission: false }).catch((err) => {
      console.warn("Push registration failed:", err?.message || err);
    });
  }, [user?._id, user?.isGuest]);

  /* ---------------------------------------------------
     INITIAL AUTH CHECK
  --------------------------------------------------- */
  useEffect(() => {
    const verifyAuth = async () => {
      const url = new URL(window.location.href);
      const oauthToken = url.searchParams.get("oauthToken");
      if (oauthToken) {
        persistAuthToken(oauthToken);
        url.searchParams.delete("oauthToken");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }

      const token = getStoredAuthToken();
      const storedUser = localStorage.getItem("user");
      let hasHydratedFromStorage = false;

      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          hasHydratedFromStorage = true;
        } catch {
          localStorage.removeItem("user");
        }
      }

      if (!token) {
        setLoading(false);
        return;
      }

      // If a user snapshot exists, render immediately and refresh auth in background.
      if (hasHydratedFromStorage) {
        setLoading(false);
      }

      try {
        const res = await authAPI.getProfile({ timeout: 6000, skipAuthRedirect: true });
        clearManualLogoutFlag();
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          console.warn("Auth profile refresh rejected; keeping saved session until explicit logout.");
          if (!hasHydratedFromStorage) {
            clearStoredAuthToken();
            localStorage.removeItem("user");
            setUser(null);
          }
          return;
        }

        // Keep current local session on transient network delays/failures.
        console.warn("Auth profile refresh skipped:", err?.message || err);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, [handleLogout]);

  /* ---------------------------------------------------
     STALE SESSION RECOVERY
     A 401/403 from an API call (guest token deleted on
     the server, JWT_SECRET rotation, etc.) leaves the app
     stuck in a broken session. Verify once, then clear
     and return to the landing page so the user can
     re-auth or bootstrap a fresh guest session.
  --------------------------------------------------- */
  const authRejectedHandledRef = useRef(false);

  useEffect(() => {
    const handleAuthRejected = async () => {
      if (typeof window === "undefined") return;
      if (authRejectedHandledRef.current) return;
      authRejectedHandledRef.current = true;

      try {
        await authAPI.getProfile({ timeout: 6000, skipAuthRedirect: true });
        // Profile is actually valid — the rejection was transient (e.g. stale
        // in-flight request). Let the session stand.
        authRejectedHandledRef.current = false;
        return;
      } catch (err) {
        const status = err?.response?.status;
        if (status !== 401 && status !== 403) {
          authRejectedHandledRef.current = false;
          return;
        }
      }

      clearStoredAuthToken();
      localStorage.removeItem("user");
      setUser(null);
      setLoading(false);
      window.location.replace("/");
    };

    window.addEventListener("task-suite:auth-rejected", handleAuthRejected);
    return () => window.removeEventListener("task-suite:auth-rejected", handleAuthRejected);
  }, []);

  /* ---------------------------------------------------
     LOGIN
  --------------------------------------------------- */
  const login = useCallback(async (email, password, options = {}) => {
    const res = await authAPI.login({ email, password });
    const { token, ...userData } = res.data;

    persistAuthToken(token);
    clearManualLogoutFlag();
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);

    return res.data;
  }, []);

  /* ---------------------------------------------------
     REGISTER
  --------------------------------------------------- */
  const register = useCallback(async (name, email, password, options = {}) => {
    const res = await authAPI.register({
      name,
      email,
      password,
      ...options,
    });
    const { token, ...userData } = res.data;

    persistAuthToken(token);
    clearManualLogoutFlag();
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);

    return res.data;
  }, []);

const startGuestSession = useCallback(async () => {
    setLoading(true);
    // Render cold starts can take well over the default request timeout.
    // Retry a few times before giving up so a slow boot does not kill the
    // whole guest flow for the user.
    const retryDelays = [0, 1200];
    let lastError = null;
    for (const delay of retryDelays) {
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      try {
        const res = await authAPI.guestLogin();
        const { token, ...userData } = res.data;

        persistAuthToken(token);
        clearManualLogoutFlag();
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        setLoading(false);

        return res.data;
      } catch (err) {
        lastError = err;
      }
    }

    clearStoredAuthToken();
    localStorage.removeItem("user");
    setUser(null);
    setLoading(false);
    throw lastError;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = prev ? { ...prev, ...updates } : { ...updates };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isAuthenticated = useCallback(() => {
    return !!getStoredAuthToken() && !!user;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser: updateUser,
        loading,
        login,
        register,
        startGuestSession,
        logout: handleLogout,
        socketConnected,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
